import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  Progress,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  FileTextOutlined,
  LinkOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  RobotOutlined,
  StopOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  cancelProduceRun,
  createProduceRun,
  estimateBillingCredits,
  getProduceRun,
  getProduceSource,
  ingestProduceSource,
  listProduceRuns,
  reparseProduceSource,
} from '../api';
import { CONTENT_DELIVERY_PLATFORM_OPTIONS } from '../utils/contentPlatforms';
import { formatShanghaiDateTime } from '../utils/datetime';
import { useNavigate } from 'react-router-dom';
import './ContentProduce.css';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;

const FLOW_OPTIONS = [
  { label: '一段内容 → 小红书笔记', value: 'source_to_xhs_note' },
  { label: '一段内容 → 公众号文章', value: 'source_to_wechat_article' },
  { label: '一段内容 → 短视频', value: 'source_to_short_video' },
];

const SOURCE_KIND_OPTIONS = [
  { label: '文字输入', value: 'text' },
  { label: '网页链接', value: 'url' },
  { label: '本地文档', value: 'file' },
];

const FLOW_DEFAULTS = {
  source_to_xhs_note: {
    available_platforms: ['小红书'],
    preset: '口语自然、重点明确、适合种草风格',
  },
  source_to_wechat_article: {
    available_platforms: ['微信公众号'],
    preset: '结构清晰、通俗解释、适合公众号阅读',
  },
  source_to_short_video: {
    available_platforms: ['抖音', '微信视频号', '小红书', '快手'],
    preset: '节奏紧凑、信息密度高、适合 30 秒短视频传播',
    scene_count: 6,
    duration_sec: 30,
    aspect_ratio: '9:16',
    render_video: true,
    strict_render: false,
    upload_to_oss: false,
  },
};
const CREDIT_STAGE_LABELS = {
  draft_generation: '草稿生成',
  script_generation: '脚本生成',
  standard_render: '视频渲染',
};

const RUN_ACTIVE_STATUSES = new Set(['queued', 'running', 'cancel_requested']);

const statusColorMap = {
  pending: 'gold',
  queued: 'gold',
  running: 'processing',
  succeeded: 'success',
  failed: 'error',
  canceled: 'default',
  cancel_requested: 'warning',
  active: 'processing',
};

const flowLabelMap = FLOW_OPTIONS.reduce((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

const formatStatusTag = (value) => {
  const text = String(value || '-').trim() || '-';
  const color = statusColorMap[text] || 'default';
  return <Tag color={color}>{text}</Tag>;
};

const normalizePlatformsForFlow = (flowCode) => [...(FLOW_DEFAULTS[flowCode]?.available_platforms || [])];

const normalizeRunDoc = (payload) => payload?.data || payload?.run || payload || null;

const buildRunResultSummary = (run) => {
  if (!run || typeof run !== 'object') return '-';
  const result = run.result || {};
  const createdIds = Array.isArray(result.created_content_ids) ? result.created_content_ids : [];
  if (createdIds.length > 0) return createdIds.join(', ');
  if (result?.stats?.draft) return '已生成草稿';
  if (result?.stats?.render_error) return '已生成，视频渲染失败';
  if (result?.stats?.title) return String(result.stats.title);
  if (run.error?.error_message) return String(run.error.error_message);
  return '-';
};

const ContentProduce = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [sourceFileList, setSourceFileList] = useState([]);
  const [submittingSource, setSubmittingSource] = useState(false);
  const [submittingRun, setSubmittingRun] = useState(false);
  const [creditEstimate, setCreditEstimate] = useState(null);
  const [creditEstimateLoading, setCreditEstimateLoading] = useState(false);
  const [creditEstimateError, setCreditEstimateError] = useState('');
  const [currentSource, setCurrentSource] = useState(null);
  const [currentRun, setCurrentRun] = useState(null);
  const [activeRunId, setActiveRunId] = useState('');
  const [runsLoading, setRunsLoading] = useState(false);
  const [recentRuns, setRecentRuns] = useState([]);
  const [runsPagination, setRunsPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const pollTimerRef = useRef(null);
  const estimateTokenRef = useRef(0);

  const sourceKind = Form.useWatch('source_kind', form) || 'text';
  const flowCode = Form.useWatch('flow_code', form) || 'source_to_xhs_note';
  const renderVideo = Form.useWatch('render_video', form);
  const sceneCount = Form.useWatch('scene_count', form);
  const durationSec = Form.useWatch('duration_sec', form);
  const aspectRatio = Form.useWatch('aspect_ratio', form);
  const imageStyle = Form.useWatch('image_style', form);
  const isShortVideoFlow = flowCode === 'source_to_short_video';

  const resetPolling = useCallback(() => {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const loadRuns = useCallback(async (page = 1, pageSize = 10, silent = false) => {
    if (!silent) setRunsLoading(true);
    try {
      const resp = await listProduceRuns({ page, page_size: pageSize });
      const data = resp?.data || {};
      setRecentRuns(Array.isArray(data.items) ? data.items : []);
      setRunsPagination({
        page: Number(data.page || page),
        pageSize: Number(data.page_size || pageSize),
        total: Number(data.total || 0),
      });
    } catch (error) {
      if (!silent) message.error(error.message || '获取生产运行列表失败');
    } finally {
      if (!silent) setRunsLoading(false);
    }
  }, []);

  const fetchRunDetail = useCallback(async (runId, { silent = false } = {}) => {
    const id = String(runId || '').trim();
    if (!id) return null;
    try {
      const resp = await getProduceRun(id);
      const doc = normalizeRunDoc(resp);
      setCurrentRun(doc);
      if (doc && !RUN_ACTIVE_STATUSES.has(String(doc.status || '').trim())) {
        setActiveRunId('');
        resetPolling();
        if (doc.source_id) {
          const sourceResp = await getProduceSource(doc.source_id);
          setCurrentSource(sourceResp?.data || null);
        }
        await loadRuns(runsPagination.page, runsPagination.pageSize, true);
      }
      return doc;
    } catch (error) {
      if (!silent) message.error(error.message || '获取运行详情失败');
      return null;
    }
  }, [loadRuns, resetPolling, runsPagination.page, runsPagination.pageSize]);

  useEffect(() => {
    form.setFieldsValue({
      source_kind: 'text',
      flow_code: 'source_to_xhs_note',
      auto_parse: true,
      auto_save_to_content: false,
      title_hint: '',
      preset: FLOW_DEFAULTS.source_to_xhs_note.preset,
      available_platforms: normalizePlatformsForFlow('source_to_xhs_note'),
      scene_count: FLOW_DEFAULTS.source_to_short_video.scene_count,
      duration_sec: FLOW_DEFAULTS.source_to_short_video.duration_sec,
      aspect_ratio: FLOW_DEFAULTS.source_to_short_video.aspect_ratio,
      render_video: FLOW_DEFAULTS.source_to_short_video.render_video,
      strict_render: FLOW_DEFAULTS.source_to_short_video.strict_render,
      upload_to_oss: FLOW_DEFAULTS.source_to_short_video.upload_to_oss,
    });
    loadRuns();
  }, [form, loadRuns]);

  useEffect(() => {
    if (!activeRunId) {
      resetPolling();
      return undefined;
    }
    resetPolling();
    pollTimerRef.current = window.setInterval(() => {
      fetchRunDetail(activeRunId, { silent: true });
    }, 2500);
    fetchRunDetail(activeRunId, { silent: true });
    return () => resetPolling();
  }, [activeRunId, fetchRunDetail, resetPolling]);

  useEffect(() => () => resetPolling(), [resetPolling]);

  const handleValuesChange = (changedValues) => {
    if (!Object.prototype.hasOwnProperty.call(changedValues, 'flow_code')) return;
    const nextFlow = changedValues.flow_code;
    const defaults = FLOW_DEFAULTS[nextFlow] || {};
    form.setFieldsValue({
      preset: defaults.preset || '',
      available_platforms: normalizePlatformsForFlow(nextFlow),
      ...(nextFlow === 'source_to_short_video'
        ? {
          scene_count: defaults.scene_count,
          duration_sec: defaults.duration_sec,
          aspect_ratio: defaults.aspect_ratio,
          render_video: defaults.render_video,
          strict_render: defaults.strict_render,
          upload_to_oss: defaults.upload_to_oss,
        }
        : {}),
    });
  };

  const handleCreateSource = async () => {
    try {
      const values = await form.validateFields();
      if (values.source_kind === 'file' && sourceFileList.length === 0) {
        message.warning('请先选择一个文件');
        return;
      }

      setSubmittingSource(true);
      let resp;
      if (values.source_kind === 'file') {
        const file = sourceFileList[0]?.originFileObj;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title_hint', values.title_hint || '');
        formData.append('auto_parse', values.auto_parse ? 'true' : 'false');
        resp = await ingestProduceSource(formData);
      } else {
        resp = await ingestProduceSource({
          source_kind: values.source_kind,
          text: values.text || '',
          url: values.url || '',
          title_hint: values.title_hint || '',
          auto_parse: Boolean(values.auto_parse),
        });
      }
      const source = resp?.data || null;
      setCurrentSource(source);
      setCurrentRun(null);
      await loadRuns(1, runsPagination.pageSize, true);
      if (source?.parse_run_id) {
        setActiveRunId(String(source.parse_run_id));
        message.success('素材已创建，正在解析');
      } else {
        setActiveRunId('');
        message.success('素材已创建');
      }
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message || '创建素材失败');
    } finally {
      setSubmittingSource(false);
    }
  };

  const handleReparseSource = useCallback(async () => {
    const sourceId = String(currentSource?.source_id || '').trim();
    if (!sourceId) return;
    try {
      const resp = await reparseProduceSource(sourceId);
      setActiveRunId(String(resp?.run_id || ''));
      setCurrentRun(null);
      message.success('已提交重新解析');
    } catch (error) {
      message.error(error.message || '重新解析失败');
    }
  }, [currentSource?.source_id]);

  const buildRunOptions = useCallback((values, flow = flowCode) => {
    const options = {
      available_platforms: Array.isArray(values.available_platforms) ? values.available_platforms : [],
    };
    if (values.author) options.author = values.author;
    if (flow === 'source_to_short_video') {
      options.scene_count = Number(values.scene_count || FLOW_DEFAULTS.source_to_short_video.scene_count);
      options.duration_sec = Number(values.duration_sec || FLOW_DEFAULTS.source_to_short_video.duration_sec);
      options.aspect_ratio = values.aspect_ratio || FLOW_DEFAULTS.source_to_short_video.aspect_ratio;
      options.render_video = Boolean(values.render_video);
      options.strict_render = Boolean(values.strict_render);
      options.upload_to_oss = Boolean(values.upload_to_oss);
      if (values.image_style) options.image_style = values.image_style;
    }
    return options;
  }, [flowCode]);

  const loadCreditEstimate = useCallback(async (values, { silent = false } = {}) => {
    const nextValues = values && typeof values === 'object' ? values : form.getFieldsValue(true);
    const nextFlow = String(nextValues.flow_code || flowCode || '').trim();
    if (!nextFlow) {
      setCreditEstimate(null);
      setCreditEstimateError('');
      return null;
    }

    const token = estimateTokenRef.current + 1;
    estimateTokenRef.current = token;
    setCreditEstimateError('');
    setCreditEstimateLoading(true);
    try {
      const resp = await estimateBillingCredits({
        flow_code: nextFlow,
        options: buildRunOptions(nextValues, nextFlow),
      });
      if (token !== estimateTokenRef.current) return null;
      if (resp?.status !== 'success') {
        throw new Error(resp?.error_message || '获取积分预估失败');
      }
      setCreditEstimate(resp);
      return resp;
    } catch (error) {
      if (token !== estimateTokenRef.current) return null;
      setCreditEstimate(null);
      setCreditEstimateError(error.message || '获取积分预估失败');
      if (!silent) {
        message.error(error.message || '获取积分预估失败');
      }
      return null;
    } finally {
      if (token === estimateTokenRef.current) {
        setCreditEstimateLoading(false);
      }
    }
  }, [buildRunOptions, flowCode, form]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadCreditEstimate(form.getFieldsValue(true), { silent: true });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [aspectRatio, durationSec, flowCode, form, imageStyle, loadCreditEstimate, renderVideo, sceneCount]);

  const handleCreateRun = useCallback(async () => {
    const sourceId = String(currentSource?.source_id || '').trim();
    if (!sourceId) {
      message.warning('请先创建素材源');
      return;
    }
    if (String(currentSource?.parse_status || '').trim() !== 'succeeded') {
      message.warning('素材尚未解析完成，请等待或重新解析');
      return;
    }

    try {
      const values = await form.validateFields();
      const estimateResp = await loadCreditEstimate(values, { silent: true });
      if (!estimateResp) {
        throw new Error('获取积分预估失败，请稍后重试');
      }
      if (!estimateResp?.enough) {
        message.warning('当前可用积分不足，请先前往订阅与积分页补足后再生产');
        return;
      }
      setSubmittingRun(true);
      const resp = await createProduceRun({
        flow_code: values.flow_code,
        source_id: sourceId,
        preset: values.preset || '',
        auto_save_to_content: Boolean(values.auto_save_to_content),
        options: buildRunOptions(values, values.flow_code),
      });
      const runId = String(resp?.run_id || '').trim();
      if (!runId) throw new Error('run_id_missing');
      setActiveRunId(runId);
      setCurrentRun(null);
      await loadRuns(1, runsPagination.pageSize, true);
      message.success('已提交内容生产任务');
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message || '创建内容生产任务失败');
    } finally {
      setSubmittingRun(false);
    }
  }, [buildRunOptions, currentSource?.parse_status, currentSource?.source_id, form, loadCreditEstimate, loadRuns, runsPagination.pageSize]);

  const handleCancelRun = useCallback(async (runId) => {
    const id = String(runId || currentRun?.run_id || '').trim();
    if (!id) return;
    try {
      await cancelProduceRun(id);
      message.success('已提交取消请求');
      setActiveRunId(id);
      fetchRunDetail(id, { silent: true });
      loadRuns(runsPagination.page, runsPagination.pageSize, true);
    } catch (error) {
      message.error(error.message || '取消运行失败');
    }
  }, [currentRun?.run_id, fetchRunDetail, loadRuns, runsPagination.page, runsPagination.pageSize]);

  const currentProgress = currentRun?.progress || null;
  const currentDraft = currentRun?.result?.stats?.draft || null;
  const currentCreatedIds = Array.isArray(currentRun?.result?.created_content_ids)
    ? currentRun.result.created_content_ids
    : [];
  const estimateSummary = creditEstimate?.estimate || null;
  const estimateBalance = creditEstimate?.balance || null;
  const estimateBreakdown = Object.entries(estimateSummary?.breakdown || {});

  const runColumns = useMemo(() => ([
    {
      title: '流程',
      dataIndex: 'flow_code',
      key: 'flow_code',
      width: 220,
      render: (value) => flowLabelMap[value] || value || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (value) => formatStatusTag(value),
    },
    {
      title: '素材 ID',
      dataIndex: 'source_id',
      key: 'source_id',
      width: 220,
      ellipsis: true,
    },
    {
      title: '结果',
      key: 'result',
      ellipsis: true,
      render: (_, record) => buildRunResultSummary(record),
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: (value) => formatShanghaiDateTime(value),
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            onClick={() => {
              setCurrentRun(record);
              setActiveRunId(RUN_ACTIVE_STATUSES.has(String(record?.status || '').trim()) ? String(record.run_id || '') : '');
              fetchRunDetail(record.run_id, { silent: true });
            }}
          >
            查看
          </Button>
          {RUN_ACTIVE_STATUSES.has(String(record?.status || '').trim()) && (
            <Button size="small" danger onClick={() => handleCancelRun(record.run_id)}>
              取消
            </Button>
          )}
        </Space>
      ),
    },
  ]), [fetchRunDetail, handleCancelRun]);

  return (
    <div className="page-shell">
      <Card className="page-card" bodyStyle={{ padding: 0 }}>
        <div className="page-panel produce-page">
          <div className="page-panel__top produce-page__top">
            <div>
              <Space align="center" size={10}>
                <RobotOutlined style={{ fontSize: 20, color: '#1677ff' }} />
                <Text strong style={{ fontSize: 16 }}>生产工作台</Text>
              </Space>
              <Paragraph type="secondary" style={{ margin: '8px 0 0' }}>
                把素材转成图文或短视频草稿，并按需直接沉淀到内容库，衔接下一步管理与发布。
              </Paragraph>
            </div>
            <Space>
              <Button onClick={() => loadRuns(runsPagination.page, runsPagination.pageSize)}>刷新记录</Button>
              <Button type="primary" icon={<PlayCircleOutlined />} onClick={handleCreateRun} loading={submittingRun}>
                开始生产
              </Button>
            </Space>
          </div>

          <div className="page-panel__body produce-page__body">
            <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
              <Row gutter={[12, 12]}>
                <Col xs={24} xl={11}>
                  <Card title="1. 创建素材源" className="produce-card" extra={currentSource?.source_id ? <Text type="secondary">{currentSource.source_id}</Text> : null}>
                    <Form.Item name="source_kind" label="素材类型" rules={[{ required: true, message: '请选择素材类型' }]}>
                      <Select options={SOURCE_KIND_OPTIONS} />
                    </Form.Item>

                    {sourceKind === 'text' && (
                      <Form.Item name="text" label="原始内容" rules={[{ required: true, message: '请输入原始内容' }]} preserve={false}>
                        <TextArea rows={8} maxLength={20000} showCount placeholder="请输入一段文字，支持文章摘要、采访纪要、产品说明等。" />
                      </Form.Item>
                    )}

                    {sourceKind === 'url' && (
                      <Form.Item name="url" label="网页链接" rules={[{ required: true, message: '请输入链接' }]} preserve={false}>
                        <Input prefix={<LinkOutlined />} placeholder="https://example.com/article" />
                      </Form.Item>
                    )}

                    {sourceKind === 'file' && (
                      <Form.Item label="上传文件" required preserve={false}>
                        <Dragger
                          multiple={false}
                          maxCount={1}
                          accept=".txt,.md,.docx,.ppt,.pptx,.pdf"
                          beforeUpload={() => false}
                          fileList={sourceFileList}
                          onChange={({ fileList }) => setSourceFileList(fileList.slice(-1))}
                          onRemove={() => setSourceFileList([])}
                        >
                          <p className="ant-upload-drag-icon">
                            <UploadOutlined />
                          </p>
                          <p className="ant-upload-text">拖拽文件到这里，或点击上传</p>
                          <p className="ant-upload-hint">支持 txt / markdown / word / ppt / pdf</p>
                        </Dragger>
                      </Form.Item>
                    )}

                    <Form.Item name="title_hint" label="标题提示（可选）">
                      <Input placeholder="可输入素材标题、主题或希望突出表达的角度" />
                    </Form.Item>

                    <Form.Item name="auto_parse" label="创建后自动解析" valuePropName="checked">
                      <Switch checkedChildren="自动解析" unCheckedChildren="手动解析" />
                    </Form.Item>

                    <Space>
                      <Button type="primary" icon={<FileTextOutlined />} onClick={handleCreateSource} loading={submittingSource}>
                        创建素材源
                      </Button>
                      {currentSource?.source_id && (
                        <Button icon={<ReloadOutlined />} onClick={handleReparseSource}>
                          重新解析
                        </Button>
                      )}
                    </Space>
                  </Card>
                </Col>

                <Col xs={24} xl={13}>
                  <Card title="2. 生产配置与状态" className="produce-card">
                    <Row gutter={12}>
                      <Col xs={24} lg={12}>
                        <Form.Item name="flow_code" label="生成流程" rules={[{ required: true, message: '请选择生成流程' }]}>
                          <Select options={FLOW_OPTIONS} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Form.Item name="preset" label="风格预设">
                          <Input placeholder="例如：通俗、克制、适合知识分享" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={12}>
                      <Col xs={24} lg={12}>
                        <Form.Item name="available_platforms" label="目标平台">
                          <Select mode="multiple" options={CONTENT_DELIVERY_PLATFORM_OPTIONS} placeholder="选择目标平台" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Form.Item name="author" label="作者名（可选）">
                          <Input placeholder="例如：Publishy AI" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item name="auto_save_to_content" label="生成后保存到内容库" valuePropName="checked">
                      <Switch checkedChildren="保存内容库" unCheckedChildren="仅生成草稿" />
                    </Form.Item>

                    {isShortVideoFlow && (
                      <Collapse
                        bordered={false}
                        className="produce-collapse"
                        items={[
                          {
                            key: 'video-advanced',
                            label: '短视频高级配置',
                            children: (
                              <>
                                <Row gutter={12}>
                                  <Col xs={24} lg={8}>
                                    <Form.Item name="scene_count" label="分镜数量">
                                      <InputNumber min={3} max={10} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} lg={8}>
                                    <Form.Item name="duration_sec" label="目标时长（秒）">
                                      <InputNumber min={8} max={180} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} lg={8}>
                                    <Form.Item name="aspect_ratio" label="画幅比例">
                                      <Select
                                        options={[
                                          { label: '竖屏 9:16', value: '9:16' },
                                          { label: '横屏 16:9', value: '16:9' },
                                          { label: '方形 1:1', value: '1:1' },
                                          { label: '4:3', value: '4:3' },
                                          { label: '3:4', value: '3:4' },
                                        ]}
                                      />
                                    </Form.Item>
                                  </Col>
                                </Row>
                                <Row gutter={12}>
                                  <Col xs={24} lg={12}>
                                    <Form.Item name="image_style" label="画面风格（可选）">
                                      <Input placeholder="例如：科技感插画、真实电影感、极简信息图" />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} lg={12}>
                                    <Space wrap className="produce-switches">
                                      <Form.Item name="render_video" label="渲染视频" valuePropName="checked" style={{ marginBottom: 0 }}>
                                        <Switch checkedChildren="渲染" unCheckedChildren="仅脚本" />
                                      </Form.Item>
                                      <Form.Item name="strict_render" label="渲染失败即报错" valuePropName="checked" style={{ marginBottom: 0 }}>
                                        <Switch checkedChildren="严格" unCheckedChildren="容错" />
                                      </Form.Item>
                                      <Form.Item name="upload_to_oss" label="视频上传 OSS" valuePropName="checked" style={{ marginBottom: 0 }}>
                                        <Switch checkedChildren="上传" unCheckedChildren="本地" />
                                      </Form.Item>
                                    </Space>
                                  </Col>
                                </Row>
                              </>
                            ),
                          },
                        ]}
                      />
                    )}

                    <div className="produce-credit-panel">
                      <div className="produce-credit-panel__header">
                        <Text strong>积分预估</Text>
                        {creditEstimateLoading ? <Text type="secondary">计算中...</Text> : null}
                      </div>
                      {creditEstimateError ? (
                        <Alert
                          type="error"
                          showIcon
                          className="produce-inline-alert"
                          message="积分预估失败"
                          description={creditEstimateError}
                        />
                      ) : null}
                      {!creditEstimateError && estimateSummary && estimateBalance ? (
                        <>
                          <div className="produce-credit-metrics">
                            <div className="produce-credit-metric">
                              <Text type="secondary">预计消耗</Text>
                              <Text strong>{Number(estimateSummary.total || 0)} 积分</Text>
                            </div>
                            <div className="produce-credit-metric">
                              <Text type="secondary">当前可用</Text>
                              <Text strong>{Number(estimateBalance.total_spendable_balance || 0)} 积分</Text>
                            </div>
                            <div className="produce-credit-metric">
                              <Text type="secondary">会员赠送</Text>
                              <Text strong>{Number(estimateBalance.gift_balance || 0)} 积分</Text>
                            </div>
                            <div className="produce-credit-metric">
                              <Text type="secondary">购买积分</Text>
                              <Text strong>{Number(estimateBalance.paid_balance || 0)} 积分</Text>
                            </div>
                          </div>
                          {estimateBreakdown.length > 0 ? (
                            <Space wrap>
                              {estimateBreakdown.map(([key, amount]) => (
                                <Tag key={key} color="blue">
                                  {CREDIT_STAGE_LABELS[key] || key}: {Number(amount || 0)}
                                </Tag>
                              ))}
                            </Space>
                          ) : null}
                          <Alert
                            type={creditEstimate?.enough ? 'info' : 'warning'}
                            showIcon
                            className="produce-inline-alert"
                            message={creditEstimate?.enough ? '积分充足，可直接开始生产' : '当前积分不足'}
                            description={creditEstimate?.enough
                              ? '提交前系统会再次校验一次，避免并发情况下的余额误判。'
                              : '请先前往订阅与积分页开通会员或购买积分包，再继续生产。'}
                            action={!creditEstimate?.enough ? (
                              <Button size="small" type="primary" onClick={() => navigate('/billing')}>
                                去补充积分
                              </Button>
                            ) : null}
                          />
                        </>
                      ) : null}
                      {!creditEstimateError && !estimateSummary && !creditEstimateLoading ? (
                        <Text type="secondary">填写生成配置后，系统会自动计算本次生产预计消耗的积分。</Text>
                      ) : null}
                    </div>

                    <div className="produce-status-grid">
                      <div className="produce-status-panel">
                        <Text strong>当前素材</Text>
                        {currentSource ? (
                          <Descriptions size="small" column={1} className="produce-descriptions">
                            <Descriptions.Item label="标题">{currentSource.title || currentSource.title_hint || '-'}</Descriptions.Item>
                            <Descriptions.Item label="类型">{currentSource.source_kind || '-'}</Descriptions.Item>
                            <Descriptions.Item label="解析状态">{formatStatusTag(currentSource.parse_status)}</Descriptions.Item>
                            <Descriptions.Item label="字数">{currentSource.word_count ?? '-'}</Descriptions.Item>
                            <Descriptions.Item label="更新时间">{formatShanghaiDateTime(currentSource.updated_at)}</Descriptions.Item>
                          </Descriptions>
                        ) : (
                          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="尚未创建素材源" />
                        )}
                        {currentSource?.canonical_preview_text && (
                          <Alert
                            type="info"
                            showIcon
                            className="produce-inline-alert"
                            message="素材预览"
                            description={currentSource.canonical_preview_text}
                          />
                        )}
                      </div>

                      <div className="produce-status-panel">
                        <Text strong>当前运行</Text>
                        {currentRun ? (
                          <>
                            <Space size={8} wrap style={{ marginTop: 8, marginBottom: 8 }}>
                              {formatStatusTag(currentRun.status)}
                              <Text type="secondary">{currentRun.flow_code || currentRun.producer_name}</Text>
                              <Text type="secondary">{formatShanghaiDateTime(currentRun.updated_at)}</Text>
                              {RUN_ACTIVE_STATUSES.has(String(currentRun.status || '').trim()) && (
                                <Button size="small" danger icon={<StopOutlined />} onClick={() => handleCancelRun(currentRun.run_id)}>
                                  取消运行
                                </Button>
                              )}
                            </Space>

                            {typeof currentProgress?.percent === 'number' && (
                              <Progress percent={currentProgress.percent} status={String(currentRun.status || '') === 'failed' ? 'exception' : undefined} />
                            )}

                            {(currentProgress?.message || currentProgress?.step) && (
                              <Alert
                                className="produce-inline-alert"
                                type="info"
                                showIcon
                                message={currentProgress?.step || '处理中'}
                                description={currentProgress?.message || ''}
                              />
                            )}

                            {currentRun?.error?.error_message && (
                              <Alert
                                className="produce-inline-alert"
                                type="error"
                                showIcon
                                message="运行失败"
                                description={currentRun.error.error_message}
                              />
                            )}

                            {currentCreatedIds.length > 0 && (
                              <Alert
                                className="produce-inline-alert"
                                type="success"
                                showIcon
                                message="已保存到内容库"
                                description={currentCreatedIds.join('，')}
                              />
                            )}

                            {currentDraft && (
                              <Collapse
                                className="produce-collapse"
                                items={[
                                  {
                                    key: 'draft-preview',
                                    label: '草稿预览',
                                    children: (
                                      <div className="produce-draft-preview">
                                        <Text strong>{currentDraft.title || '-'}</Text>
                                        {currentDraft.summary && <Paragraph>{currentDraft.summary}</Paragraph>}
                                        {Array.isArray(currentDraft.tags) && currentDraft.tags.length > 0 && (
                                          <Space wrap>
                                            {currentDraft.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)}
                                          </Space>
                                        )}
                                        {currentDraft.body && <pre className="produce-pre">{currentDraft.body}</pre>}
                                        {currentDraft.body_html && <pre className="produce-pre">{currentDraft.body_html}</pre>}
                                        {Array.isArray(currentDraft.media_urls) && currentDraft.media_urls.length > 0 && (
                                          <div className="produce-media-list">
                                            {currentDraft.media_urls.map((url) => (
                                              <a key={url} href={url} target="_blank" rel="noreferrer">{url}</a>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    ),
                                  },
                                ]}
                              />
                            )}

                            {(currentRun?.result?.stats?.render_error || currentRun?.result?.stats?.title) && !currentDraft && (
                              <Collapse
                                className="produce-collapse"
                                items={[
                                  {
                                    key: 'run-json',
                                    label: '运行结果详情',
                                    children: <pre className="produce-pre">{JSON.stringify(currentRun.result || {}, null, 2)}</pre>,
                                  },
                                ]}
                              />
                            )}
                          </>
                        ) : (
                          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无运行记录" />
                        )}
                      </div>
                    </div>
                  </Card>
                </Col>
              </Row>
            </Form>

            <Card title="最近运行" className="produce-card produce-card--table">
              <Table
                rowKey="run_id"
                columns={runColumns}
                dataSource={recentRuns}
                loading={runsLoading}
                pagination={{
                  current: runsPagination.page,
                  pageSize: runsPagination.pageSize,
                  total: runsPagination.total,
                  showSizeChanger: true,
                  onChange: (page, pageSize) => loadRuns(page, pageSize),
                }}
                scroll={{ x: 980 }}
                onRow={(record) => ({
                  onClick: () => {
                    setCurrentRun(record);
                    setActiveRunId(RUN_ACTIVE_STATUSES.has(String(record?.status || '').trim()) ? String(record.run_id || '') : '');
                    fetchRunDetail(record.run_id, { silent: true });
                  },
                })}
              />
            </Card>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ContentProduce;
